import { Component, EventEmitter, inject, Output } from '@angular/core';
import { HistoryBook } from '../signals/+models/history-book.model';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { HISTORY_BOOK_STORE } from '../signals/+store/history-book.store';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import * as XLSX from 'xlsx';

@Component({
    selector: 'app-history-book',
    standalone: true,
    imports: [
        MatTableModule,
        MatButtonModule,
        CommonModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatMenuModule,
        FormsModule,
        MatListModule,
    ],
    templateUrl: './history-book.component.html',
    styleUrls: ['./history-book.component.scss']
})
export class HistoryBookComponent {
  readonly historyBookStore = inject(HISTORY_BOOK_STORE);

  @Output() close = new EventEmitter();

  searchTerm: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';

  get HistoryBook(): HistoryBook[] {
    const projectId = this.historyBookStore.currentProjectId();
    return projectId ? this.historyBookStore.getHistory()[projectId] || [] : [];
  };

  contentParsed(content: string): { key: string; value: string }[] {
    try {
      const contentParsed = JSON.parse(content);
      return Object.keys(contentParsed).map((key) => ({
        key,
        value: contentParsed[key],
      }));
    } catch {
      console.warn('Invalid JSON content:', content);
      return [];
    }
  }

  contentFormatted(parsedContent: { key: string; value: string }[]): string {
    try {
      return parsedContent
        .map((item) => `${item.key}: ${item.value}`)
        .join('\n');
    } catch (error) {
      console.warn('Invalid JSON content:', parsedContent);
      return '';
    }
  }

  htmlToText(html: string): string {
    if (!html) return '';
  
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
  
    return tempElement.textContent || tempElement.innerText || '';
  }  

  private convertToXLSX(history: HistoryBook[]): XLSX.WorkBook {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
      history.map((data: HistoryBook) => {
        const parsedContent = this.contentParsed(data.content);
        const keys = parsedContent.map((item) => item.key);
        const values = parsedContent.map((item) => {
          return /<\/?[a-z][\s\S]*>/i.test(item.value) ? this.htmlToText(item.value) : item.value;
        });        
        return {
          Date: data.date,
          Time: data.time,
          ...keys.reduce((acc: { [key: string]: string }, key, index) => {
            acc[key] = values[index];
            return acc;
          }, {}),
        };
      })
    );

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'History');
    return wb;
  }

  downloadHistory(): void {
    const wb = this.convertToXLSX(this.HistoryBook);
    const fileName = `history-book_${new Date().toISOString()}.xlsx`;

    // Export to XLSX
    XLSX.writeFile(wb, fileName);
  }

  get filteredActions(): HistoryBook[] {
    const filtered = this.HistoryBook.filter((action) => {
      const search = this.searchTerm.trim().toLowerCase();
      const parsedContent = this.contentParsed(action.content);
      const contentFormatted = this.contentFormatted(parsedContent);
      return (
        action.action?.toLowerCase().includes(search) ||
        contentFormatted?.toLowerCase().includes(search)
      );
    });

    return this.sortOrder === 'desc' ? filtered : filtered.reverse();
  }

  setSortOrder(order: 'asc' | 'desc'): void {
    this.sortOrder = order;
  }

  clearHistory(): void {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete the history for this project? This action cannot be undone.'
    );
  
    if (confirmDelete) {
      this.historyBookStore.clearHistory();
    }
  }

  get hasHistory(): boolean {
    return this.HistoryBook.length > 0;
  }  

  closeHistory(){
    this.close.emit(true)
  }
  
}
